import React, { useState } from "react";

const formatAmount = (value) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(Number(value || 0));

const formatDuration = (milliseconds) => {
  const totalMinutes = Math.round(Number(milliseconds || 0) / 60000);
  if (totalMinutes <= 0) return "-";
  if (totalMinutes < 60) return `${totalMinutes} min`;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes ? `${hours} hr ${minutes} min` : `${hours} hr`;
};

const formatIstDateTime = (date) => {
  if (!date) return "-";

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return "-";

  return parsedDate.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getBudgetLabel = (status) => {
  if (status === "WITHIN_BUDGET") return "Within budget";
  if (status === "AT_BUDGET") return "At budget";
  if (status === "BEYOND_BUDGET") return "Beyond budget";
  return "Budget not defined";
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getLatestQuotationSet = (supplier = {}) =>
  supplier?.vendorQuotations?.quotationSets?.slice(-1)?.[0] || {};

const getSupplierQuotationAmountWithoutGst = (supplier = {}) => {
  const latestSet = getLatestQuotationSet(supplier);
  const grandTotalWithoutGst = supplier?.vendorQuotations?.grandTotalWithoutGst;

  if (Number(grandTotalWithoutGst) > 0) return toNumber(grandTotalWithoutGst);

  const latestTotal = toNumber(latestSet?.totalWithoutGst) + toNumber(latestSet?.othersTotalWithoutGst);
  return latestTotal > 0 ? latestTotal : toNumber(latestSet?.totalWithoutGst);
};

const getFirstQuotationAmountWithoutGst = (supplier = {}) => {
  const firstSet = supplier?.vendorQuotations?.quotationSets?.[0];
  if (!firstSet) return 0;

  return toNumber(firstSet?.totalWithoutGst) + toNumber(firstSet?.othersTotalWithoutGst);
};

const getRfqSavingsInsight = (rfqData = {}) => {
  const suppliers = rfqData?.suppliers || [];
  const quotedSuppliers = suppliers
    .filter((supplier) => supplier?.quotationReceived)
    .map((supplier) => ({
      supplierName: supplier?.businessName || supplier?.supplierName || supplier?.supplierId || "-",
      amount: getFirstQuotationAmountWithoutGst(supplier),
    }))
    .filter((supplier) => supplier.amount > 0)
    .sort((a, b) => a.amount - b.amount);

  const awardedSuppliers = suppliers.filter((supplier) => supplier?.isAwarded);
  const finalAwardedAmount = awardedSuppliers.reduce(
    (sum, supplier) => sum + toNumber(supplier?.closingDetails?.[0]?.amount),
    0,
  );
  const firstRoundL1 = quotedSuppliers[0] || null;
  const savings = firstRoundL1 ? Math.max(firstRoundL1.amount - finalAwardedAmount, 0) : 0;

  return {
    firstRoundL1,
    finalAwardedAmount,
    savings,
    awardedVendorNames: awardedSuppliers
      .map((supplier) => supplier?.businessName || supplier?.supplierName || supplier?.supplierId)
      .filter(Boolean),
    hasBasis: Boolean(firstRoundL1 && awardedSuppliers.length),
  };
};

const RfqSavingsBasisSection = ({ rfqData }) => {
  if (String(rfqData?.status || "").toUpperCase() !== "CLOSED") return null;

  const insight = getRfqSavingsInsight(rfqData);

  return (
    <div className="mb-5 overflow-hidden rounded-xl border border-green-100 bg-white shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-green-50 bg-gradient-to-r from-green-50 to-white px-5 py-4">
        <div>
          <h3 className="text-sm font-semibold text-[#1F1E29]">Savings amount basis</h3>
          <p className="mt-1 text-xs text-gray-500">
            First-round L1 quotation compared with final closed / awarded amount.
          </p>
        </div>
        {insight.awardedVendorNames.length > 0 && (
          <span className="rounded-full border border-green-200 bg-white px-3 py-1 text-[11px] font-semibold text-green-700">
            {insight.awardedVendorNames.length === 1
              ? insight.awardedVendorNames[0]
              : `${insight.awardedVendorNames[0]} +${insight.awardedVendorNames.length - 1}`}
          </span>
        )}
      </div>

      {insight.hasBasis ? (
        <div className="p-5">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-xl border border-green-100 bg-green-50 p-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-green-700">First-round L1</p>
              <p className="mt-2 text-xl font-semibold text-green-700">Rs. {formatAmount(insight.firstRoundL1.amount)}</p>
              <p className="mt-1 truncate text-xs font-medium text-gray-600">{insight.firstRoundL1.supplierName}</p>
            </div>
            <div className="rounded-xl border border-orange-100 bg-orange-50 p-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-orange-700">Final awarded</p>
              <p className="mt-2 text-xl font-semibold text-orange-700">Rs. {formatAmount(insight.finalAwardedAmount)}</p>
              <p className="mt-1 text-xs font-medium text-gray-600">Closed RFQ amount</p>
            </div>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">Savings</p>
              <p className="mt-2 text-xl font-semibold text-emerald-700">Rs. {formatAmount(insight.savings)}</p>
              <p className="mt-1 text-xs font-medium text-gray-600">Shown for this RFQ</p>
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-gray-100 bg-white px-4 py-4 text-center">
            <p className="text-xs font-semibold text-gray-500">First-round L1 amount - Final awarded amount</p>
            <p className="mt-2 text-base font-semibold text-[#1F1E29]">
              Rs. {formatAmount(insight.firstRoundL1.amount)} - Rs. {formatAmount(insight.finalAwardedAmount)} = Rs. {formatAmount(insight.savings)}
            </p>
          </div>
        </div>
      ) : (
        <div className="p-5 text-sm text-gray-500">
          Saving basis is not available because first-round L1 quotation or awarded amount is missing.
        </div>
      )}
    </div>
  );
};
const getLowestHighestQuotationInsight = (suppliers = []) => {
  const quotedSuppliers = (suppliers || [])
    .filter((supplier) => supplier?.quotationReceived)
    .map((supplier) => ({
      supplierId: supplier?.supplierId,
      supplierName: supplier?.businessName || supplier?.supplierName || supplier?.supplierId || "-",
      amount: getSupplierQuotationAmountWithoutGst(supplier),
    }))
    .filter((supplier) => supplier.amount > 0)
    .sort((a, b) => a.amount - b.amount);

  if (quotedSuppliers.length < 2) return null;

  const lowest = quotedSuppliers[0];
  const highest = quotedSuppliers[quotedSuppliers.length - 1];
  const difference = highest.amount - lowest.amount;
  const differencePercentage = lowest.amount > 0 ? (difference / lowest.amount) * 100 : 0;

  return {
    lowest,
    highest,
    difference,
    differencePercentage,
    quotedVendorCount: quotedSuppliers.length,
  };
};

const hasApprovalStarted = (rfqData = {}) => {
  const status = String(rfqData?.status || "").toUpperCase();
  return (
    ["PENDING APPROVAL", "APPROVED", "CLOSED", "REWORK"].includes(status) ||
    (rfqData?.approvalDetails || []).length > 0
  );
};

const SummaryItem = ({ label, value, detail, className = "bg-white" }) => (
  <div className={`border border-gray-200 px-4 py-3 ${className}`}>
    <p className="text-[11px] font-medium uppercase text-gray-500">{label}</p>
    <p className="mt-1 text-base font-semibold text-[#1F1E29]">{value}</p>
    {detail && <p className="mt-1 text-xs text-gray-500">{detail}</p>}
  </div>
);

const LowestHighestQuotationCard = ({ insight }) => {
  if (!insight) return null;

  return (
    <div className="mt-3 overflow-hidden border border-blue-100 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-blue-50 bg-blue-50 px-4 py-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-800">
            Lowest vs Highest Quotation
          </p>
          <p className="mt-0.5 text-[11px] text-blue-700">
            L1 vs H1 comparison across {insight.quotedVendorCount} quoted vendors
          </p>
        </div>
        <span className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-blue-700 ring-1 ring-blue-100">
          L1 vs H1
        </span>
      </div>

      <div className="grid grid-cols-3 overflow-hidden rounded-lg border border-gray-100">
        {/* L1 Lowest */}
        <div className="px-2 py-3 text-center">
          <p className="text-[10px] font-semibold uppercase text-gray-500">
            L1 Lowest
          </p>

          <p className="mt-1 text-xs font-bold text-green-700 md:text-sm">
            Rs. {formatAmount(insight.lowest.amount)}
          </p>

          <p
            className="mt-1 truncate text-[10px] text-gray-600 md:text-xs"
            title={insight.lowest.supplierName}
          >
            {insight.lowest.supplierName}
          </p>
        </div>

        {/* Difference */}
        <div className="border-x border-gray-200 px-2 py-3 text-center">
          <p className="text-[10px] font-semibold uppercase text-gray-500">
            Difference
          </p>

          <p className="mt-1 text-xs font-bold text-[#1F1E29] md:text-sm">
            Rs. {formatAmount(insight.difference)}
          </p>

          <p className="mt-1 text-[10px] text-gray-500 md:text-xs">
            {formatAmount(insight.differencePercentage)}% above L1
          </p>
        </div>

        {/* H1 Highest */}
        <div className="px-2 py-3 text-center">
          <p className="text-[10px] font-semibold uppercase text-gray-500">
            H1 Highest
          </p>

          <p className="mt-1 text-xs font-bold text-red-700 md:text-sm">
            Rs. {formatAmount(insight.highest.amount)}
          </p>

          <p
            className="mt-1 truncate text-[10px] text-gray-600 md:text-xs"
            title={insight.highest.supplierName}
          >
            {insight.highest.supplierName}
          </p>
        </div>
      </div>
    </div>
  );
};

const SectionTitle = ({ children, className = "" }) => (
  <h4
    className={`flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#1F1E29] ${className}`}
  >
    <span className="h-4 w-1 rounded-full bg-[#2F855A]" aria-hidden="true" />
    {children}
  </h4>
);

const MobileCollapsibleSection = ({ title, description, countLabel, children }) => {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setIsOpen((previous) => !previous)}
        className="flex w-full items-start justify-between gap-3 bg-white px-3 py-3 text-left"
      >
        <div className="min-w-0">
          <SectionTitle>{title}</SectionTitle>
          {description && (
            <p className="mt-1 text-xs leading-4 text-gray-500">{description}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {countLabel && (
            <span className="rounded-full bg-[#F2F2EA] px-2 py-1 text-[10px] font-semibold text-gray-600">
              {countLabel}
            </span>
          )}
          <svg
            className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m19 9-7 7-7-7" />
          </svg>
        </div>
      </button>
      {isOpen && (
        <div className="border-t border-gray-100 bg-[#FCFCFA] p-2">
          {children}
        </div>
      )}
    </div>
  );
};

const NegotiationTrailSection = ({ title, description, suppliers }) => {
  if (!suppliers?.length) return null;

  return (
    <div className="mt-4">
      <div className="mb-2">
        <SectionTitle>{title}</SectionTitle>
        {description && <p className="mt-1 text-xs text-gray-500">{description}</p>}
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        {suppliers.map((supplier) => (
          <div key={supplier.supplierId} className="border border-gray-200 bg-white px-3 py-3">
            <p className="text-xs font-semibold text-[#1F1E29]">
              {supplier.supplierName || supplier.supplierId}
            </p>
            <div className="mt-2 space-y-2">
              {supplier.rounds?.map((round) => (
                <div
                  key={`${supplier.supplierId}-${round.roundNumber}`}
                  className="flex flex-wrap justify-between gap-2 border-t border-gray-100 pt-2 text-xs"
                >
                  <span className="font-medium text-gray-700">{round.label}</span>
                  <span className="text-gray-700">Rs. {formatAmount(round.amount)}</span>
                  <span className={round.savings >= 0 ? "text-green-700" : "text-red-700"}>
                    {round.roundNumber === 0
                      ? "Submitted"
                      : `${round.savings >= 0 ? "Saving" : "Increase"} Rs. ${formatAmount(
                          Math.abs(round.savings)
                        )}`}
                  </span>
                  <span className="text-gray-500">{formatDuration(round.timeTakenMs)}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const getSupplierRank = (supplier, rfqSuppliers = []) => {
  const supplierData = rfqSuppliers.find(
    (item) => String(item?.supplierId) === String(supplier?.supplierId)
  );

  return supplier?.rank || supplierData?.vendorQuotations?.rank || supplierData?.rank || "";
};

const getRankNumber = (rank) => {
  const match = String(rank || "").match(/^L(\d+)$/i);
  return match ? Number(match[1]) : Number.POSITIVE_INFINITY;
};

const getSortedVendorWiseSuppliers = (suppliers = [], rfqSuppliers = []) =>
  [...(suppliers || [])].sort((a, b) => {
    const rankA = getSupplierRank(a, rfqSuppliers);
    const rankB = getSupplierRank(b, rfqSuppliers);
    const rankDiff = getRankNumber(rankA) - getRankNumber(rankB);

    if (rankDiff !== 0) return rankDiff;

    return String(a?.supplierName || a?.supplierId || "").localeCompare(
      String(b?.supplierName || b?.supplierId || "")
    );
  });

const getRoundSubmitter = (round = {}) =>
  round?.submittedBy?.toString?.().trim() || "N/A";

const isSupplierAwarded = (supplier, rfqSuppliers = []) =>
  rfqSuppliers.some(
    (item) => String(item?.supplierId) === String(supplier?.supplierId) && item?.isAwarded
  );

const getTrailTotals = (supplier) => {
  const rounds = supplier?.rounds || [];
  const firstRound = rounds[0];
  const lastRound = rounds[rounds.length - 1];
  const originalAmount = Number(supplier?.originalAmount ?? firstRound?.amount ?? 0);
  const finalAmount = Number(supplier?.finalAmount ?? lastRound?.amount ?? 0);
  const totalSavings = Number(supplier?.totalSavings ?? (originalAmount - finalAmount));

  return {
    originalAmount,
    finalAmount,
    totalSavings,
    changedRoundCount: Number(supplier?.changedRoundCount ?? rounds.filter((round) => round.roundNumber > 0).length),
  };
};

const RankBadge = ({ rank }) => {
  if (!rank) return <span className="text-[11px] text-gray-400">No rank</span>;

  return (
    <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
      {rank}
    </span>
  );
};

// eslint-disable-next-line no-unused-vars
const AwardedBadge = () => (
  <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
    <span aria-hidden="true">🏆</span>
    Awarded
  </span>
);

const AwardedIconBadge = () => (
  <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
    <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M6 2h8v2h3v3a4 4 0 0 1-4 4h-.08A5.02 5.02 0 0 1 11 12.9V15h3v2H6v-2h3v-2.1A5.02 5.02 0 0 1 7.08 11H7a4 4 0 0 1-4-4V4h3V2Zm8 4v2.83A2 2 0 0 0 15 7V6h-1ZM5 6v1a2 2 0 0 0 1 1.73V6H5Z" />
    </svg>
    Awarded
  </span>
);

const OutcomeBadge = ({ totalSavings }) => {
  if (totalSavings > 0) {
    return (
      <div className="rounded-lg bg-green-50 px-3 py-2 text-right text-green-700">
        <p className="text-[11px] font-medium">Total Saving</p>
        <p className="text-sm font-semibold">Rs. {formatAmount(totalSavings)}</p>
      </div>
    );
  }

  if (totalSavings < 0) {
    return (
      <div className="rounded-lg bg-red-50 px-3 py-2 text-right text-red-700">
        <p className="text-[11px] font-medium">Total Increase</p>
        <p className="text-sm font-semibold">Rs. {formatAmount(Math.abs(totalSavings))}</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-gray-100 px-3 py-2 text-right text-gray-600">
      <p className="text-[11px] font-medium">No negotiation</p>
      <p className="text-sm font-semibold">No change</p>
    </div>
  );
};

const VendorWiseNegotiationSection = ({ suppliers, rfqSuppliers }) => {
  if (!suppliers?.length) return null;
  const sortedSuppliers = getSortedVendorWiseSuppliers(suppliers, rfqSuppliers);

  return (
    <div className="mt-5">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <SectionTitle>Vendor-wise Negotiation</SectionTitle>
          <p className="mt-1 text-xs text-gray-500">
            All RFQ vendors who submitted quotations, including unchanged quotes and negotiated revisions.
          </p>
        </div>
        <span className="rounded-full bg-gray-100 px-3 py-1 text-[11px] font-medium text-gray-600">
          {sortedSuppliers.length} vendor{sortedSuppliers.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="grid gap-3 xl:grid-cols-2">
        {sortedSuppliers.map((supplier) => {
          const rank = getSupplierRank(supplier, rfqSuppliers);
          const awarded = isSupplierAwarded(supplier, rfqSuppliers);
          const { originalAmount, finalAmount, totalSavings, changedRoundCount } = getTrailTotals(supplier);

          return (
            <div key={supplier.supplierId} className="border border-gray-200 bg-white">
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 bg-[#FBFBF9] px-4 py-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-sm font-semibold text-[#1F1E29]">
                      {supplier.supplierName || supplier.supplierId}
                    </p>
                    <RankBadge rank={rank} />
                    {awarded && <AwardedIconBadge />}
                  </div>
                  <p className="mt-1 text-[11px] text-gray-500">
                    {changedRoundCount} negotiated round{changedRoundCount === 1 ? "" : "s"}
                  </p>
                </div>
                <OutcomeBadge totalSavings={totalSavings} />
              </div>

              <div className="grid grid-cols-[1fr_auto_1fr] items-center border-b border-gray-100 text-xs">
                <div className="px-4 py-3">
                  <p className="text-[11px] uppercase text-gray-400">First Quote</p>
                  <p className="mt-1 font-semibold text-gray-800">Rs. {formatAmount(originalAmount)}</p>
                </div>
                <div className="px-2 text-gray-300">
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M3 10a1 1 0 0 1 1-1h9.59l-3.3-3.29a1 1 0 1 1 1.42-1.42l5 5a1 1 0 0 1 0 1.42l-5 5a1 1 0 0 1-1.42-1.42L13.59 11H4a1 1 0 0 1-1-1Z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="px-4 py-3 text-right">
                  <p className="text-[11px] uppercase text-gray-400">Final Quote</p>
                  <p className="mt-1 font-semibold text-gray-800">Rs. {formatAmount(finalAmount)}</p>
                </div>
              </div>

              <div className="px-4 py-3">
                {(supplier.rounds || []).map((round) => (
                  <div
                    key={`${supplier.supplierId}-${round.roundNumber}`}
                    className="grid grid-cols-[minmax(180px,0.55fr)_1fr_auto] items-start gap-3 border-t border-gray-100 py-2 text-xs first:border-t-0"
                  >
                    <span className="min-w-0">
                      <span className="block font-medium text-gray-700">{round.label}</span>
                      <span className="mt-1 block whitespace-nowrap text-[11px] text-gray-400">
                        By: {getRoundSubmitter(round)}
                      </span>
                    </span>
                    <span className="text-gray-500">
                      {round.roundNumber === 0
                        ? "Vendor submitted first quotation"
                        : round.savings === 0
                          ? "First quote and final quote are the same"
                          : `${round.savings > 0 ? "Reduced by" : "Increased by"} Rs. ${formatAmount(Math.abs(round.savings))}`}
                    </span>
                    <div className="text-right">
                      <p className="font-semibold text-gray-800">Rs. {formatAmount(round.amount)}</p>
                      <p className={round.savings > 0 ? "text-green-700" : round.savings < 0 ? "text-red-700" : "text-gray-500"}>
                        {round.roundNumber === 0
                          ? "Submitted"
                          : round.savings === 0
                            ? "No change"
                            : `${round.savings > 0 ? "Saving" : "Increase"} Rs. ${formatAmount(Math.abs(round.savings))}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MobileSummaryCard = ({ label, value, detail, tone = "neutral" }) => {
  const valueToneClass =
    tone === "success"
      ? "text-green-700"
      : tone === "danger"
        ? "text-red-700"
        : "text-[#1F1E29]";

  return (
    <div className="rounded-xl border border-gray-200 bg-white px-3 py-3">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`mt-1 text-base font-bold ${valueToneClass}`}>{value}</p>
      {detail && <p className="mt-1 text-[11px] leading-4 text-gray-500">{detail}</p>}
    </div>
  );
};

const MobileActivityCard = ({ activity, index, getActivityDuration }) => {
  const savingClass =
    activity.savings > 0
      ? "text-green-700"
      : activity.savings < 0
        ? "text-red-700"
        : "text-gray-600";

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold text-[#1F1E29]">
            Level {activity.level || index + 1}
          </p>
          <p className="mt-1 text-[11px] text-gray-500">{activity.userName || "-"}</p>
        </div>
        <span className="rounded-full bg-[#F2F2EA] px-2 py-1 text-[10px] font-semibold text-[#303026]">
          {activity.stage === "RFQ_CREATOR_SELECTION"
            ? "Creator"
            : activity.stage === "NO_NEGOTIATION"
              ? "Approval"
              : "Negotiation"}
        </span>
      </div>

      <div className="mt-3 space-y-2 text-xs">
        <div>
          <p className="text-[10px] uppercase text-gray-400">Vendor</p>
          <p className="font-medium text-gray-700">
            {activity.supplierName || activity.supplierId || "-"}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="text-[10px] uppercase text-gray-400">Movement</p>
            <p className="font-medium text-gray-700">
              {activity.outcome === "NO_NEGOTIATION"
                ? "-"
                : `Rs. ${formatAmount(activity.previousAmount)} to Rs. ${formatAmount(
                    activity.negotiatedAmount
                  )}`}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-gray-400">Time Taken</p>
            <p className="font-medium text-gray-700">
              {formatDuration(getActivityDuration(activity))}
            </p>
          </div>
        </div>
        <div className={savingClass}>
          {activity.outcome === "SAVING"
            ? `Saving Rs. ${formatAmount(activity.savings)}`
            : activity.outcome === "INCREASE"
              ? `Increase Rs. ${formatAmount(Math.abs(activity.savings))}`
              : activity.outcome === "NO_NEGOTIATION"
                ? "No negotiation performed"
                : "No change"}
        </div>
        <p className="text-[11px] text-gray-500">
          {formatIstDateTime(activity.actionDate || activity.negotiatedAt)}
        </p>
      </div>
    </div>
  );
};

const MobileRoundWiseTrailSection = ({ suppliers }) => {
  if (!suppliers?.length) return null;

  return (
    <MobileCollapsibleSection
      title="Round-wise Trail"
      description="Selected or awarded vendor amount movement used for the approval negotiation summary."
      countLabel={`${suppliers.length} vendor${suppliers.length === 1 ? "" : "s"}`}
    >
      <div className="space-y-3">
        {suppliers.map((supplier) => (
          <div
            key={supplier.supplierId}
            className="rounded-xl border border-gray-200 bg-white px-3 py-3"
          >
            <p className="text-xs font-semibold text-[#1F1E29]">
              {supplier.supplierName || supplier.supplierId}
            </p>
            <div className="mt-2 space-y-2">
              {supplier.rounds?.map((round) => (
                <div
                  key={`${supplier.supplierId}-${round.roundNumber}`}
                  className="border-t border-gray-100 pt-2 text-xs first:border-t-0 first:pt-0"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-gray-700">{round.label}</p>
                      <p className="mt-1 text-[11px] text-gray-500">
                        {formatDuration(round.timeTakenMs)}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="font-semibold text-gray-800">
                        Rs. {formatAmount(round.amount)}
                      </p>
                      <p className={round.savings >= 0 ? "text-green-700" : "text-red-700"}>
                        {round.roundNumber === 0
                          ? "Submitted"
                          : `${round.savings >= 0 ? "Saving" : "Increase"} Rs. ${formatAmount(
                              Math.abs(round.savings)
                            )}`}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </MobileCollapsibleSection>
  );
};

const MobileVendorWiseNegotiationSection = ({ suppliers, rfqSuppliers }) => {
  if (!suppliers?.length) return null;
  const sortedSuppliers = getSortedVendorWiseSuppliers(suppliers, rfqSuppliers);

  return (
    <MobileCollapsibleSection
      title="Vendor-wise Negotiation"
      description="Vendor quote movement across submitted and negotiated rounds."
      countLabel={`${sortedSuppliers.length} vendor${sortedSuppliers.length === 1 ? "" : "s"}`}
    >
      <div className="space-y-3">
        {sortedSuppliers.map((supplier) => {
          const rank = getSupplierRank(supplier, rfqSuppliers);
          const awarded = isSupplierAwarded(supplier, rfqSuppliers);
          const { originalAmount, finalAmount, totalSavings, changedRoundCount } =
            getTrailTotals(supplier);

          return (
            <div
              key={supplier.supplierId}
              className="overflow-hidden rounded-xl border border-gray-200 bg-white"
            >
              <div className="border-b border-gray-100 bg-[#FCFCFA] px-3 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-[#1F1E29]">
                      {supplier.supplierName || supplier.supplierId || "-"}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <RankBadge rank={rank} />
                      {awarded && <AwardedIconBadge />}
                    </div>
                    <p className="mt-2 text-[11px] text-gray-500">
                      {changedRoundCount} negotiated round{changedRoundCount === 1 ? "" : "s"}
                    </p>
                  </div>
                  <OutcomeBadge totalSavings={totalSavings} />
                </div>
              </div>

              <div className="grid grid-cols-[1fr_auto_1fr] items-center border-b border-gray-100 text-xs">
                <div className="px-3 py-3">
                  <p className="text-[10px] font-semibold uppercase text-gray-400">First Quote</p>
                  <p className="mt-1 font-bold text-gray-800">Rs. {formatAmount(originalAmount)}</p>
                </div>
                <div className="text-gray-300">
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M3 10a1 1 0 0 1 1-1h9.59l-3.3-3.29a1 1 0 1 1 1.42-1.42l5 5a1 1 0 0 1 0 1.42l-5 5a1 1 0 0 1-1.42-1.42L13.59 11H4a1 1 0 0 1-1-1Z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="px-3 py-3 text-right">
                  <p className="text-[10px] font-semibold uppercase text-gray-400">Final Quote</p>
                  <p className="mt-1 font-bold text-gray-800">Rs. {formatAmount(finalAmount)}</p>
                </div>
              </div>

              <div className="px-3 py-2">
                {(supplier.rounds || []).map((round) => (
                  <div
                    key={`${supplier.supplierId}-${round.roundNumber}`}
                    className="border-t border-gray-100 py-2 text-xs first:border-t-0"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-gray-700">{round.label}</p>
                        <p className="mt-1 text-[11px] text-gray-400">
                          By {getRoundSubmitter(round)}
                        </p>
                        <p className="mt-1 text-[11px] leading-4 text-gray-500">
                          {round.roundNumber === 0
                            ? "Vendor submitted first quotation"
                            : round.savings === 0
                              ? "First quote and final quote are the same"
                              : `${round.savings > 0 ? "Reduced by" : "Increased by"} Rs. ${formatAmount(Math.abs(round.savings))}`}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="font-bold text-gray-800">Rs. {formatAmount(round.amount)}</p>
                        <p
                          className={
                            round.savings > 0
                              ? "text-green-700"
                              : round.savings < 0
                                ? "text-red-700"
                                : "text-gray-500"
                          }
                        >
                          {round.roundNumber === 0
                            ? "Submitted"
                            : round.savings === 0
                              ? "No change"
                              : `${round.savings > 0 ? "Saving" : "Increase"} Rs. ${formatAmount(Math.abs(round.savings))}`}
                        </p>
                      </div>
                    </div>
                    <p className="mt-1 text-[11px] text-gray-400">
                      Time taken: {formatDuration(round.timeTakenMs)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </MobileCollapsibleSection>
  );
};

export const MobileApprovalNegotiationSummary = ({
  rfqData,
  onBack,
  tabs,
}) => {
  const details = rfqData?.negotiationDetails;
  const approvalDetails = rfqData?.approvalDetails || [];
  const selectedVendorNames = (rfqData?.suppliers || [])
    .filter((supplier) => supplier?.isAwarded)
    .map((supplier) => supplier.businessName || supplier.supplierId)
    .filter(Boolean)
    .join(", ");
  const creatorActivities = (details?.creatorActivities || []).map((activity) => ({
    ...activity,
    level: "Creator",
  }));
  const creatorNoNegotiationActivity =
    Array.isArray(details?.creatorActivities) && creatorActivities.length === 0
      ? [{
          level: "Creator",
          userName: rfqData?.requesterName || "-",
          supplierName: selectedVendorNames || "-",
          stage: "NO_NEGOTIATION",
          outcome: "NO_NEGOTIATION",
          actionDate: rfqData?.statusLogs?.find(
            (log) => log?.changedStatusTo === "PENDING APPROVAL"
          )?.date,
        }]
      : [];
  const approvalActivities = approvalDetails.flatMap((level, levelIndex) =>
    (level?.negotiationActivities || []).map((activity) => ({
      ...activity,
      level: activity.level || level.level || levelIndex + 1,
    }))
  );
  const noNegotiationActivities = approvalDetails.flatMap((level, levelIndex) => {
    if (String(level?.status).toUpperCase() !== "APPROVED" || level?.negotiationActivities?.length) {
      return [];
    }

    const member = level?.approvedBy || level?.approvers?.[0] || {};
    return [{
      level: level.level || levelIndex + 1,
      userName: member.name || "-",
      supplierName: selectedVendorNames || "-",
      stage: "NO_NEGOTIATION",
      outcome: "NO_NEGOTIATION",
      actionDate: level?.approvedAt,
    }];
  });
  const levelActivities = [
    ...creatorActivities,
    ...creatorNoNegotiationActivity,
    ...approvalActivities,
    ...noNegotiationActivities,
  ];
  const trackedRoundCount = creatorActivities.length + approvalActivities.length;
  const hasApprovalSummary =
    levelActivities.length > 0 ||
    (details?.roundTrail || []).length > 0 ||
    Number(details?.originalAmount || 0) > 0 ||
    Number(details?.finalAgreedAmount || 0) > 0;
  const hasVendorWiseNegotiation = (details?.vendorWiseNegotiationTrail || []).length > 0;
  const budget = details?.budget || {};
  const bestNegotiator = details?.bestNegotiator;
  const negotiatedAmount = Number(details?.totalNegotiatedAmount || 0);
  const negotiationPercentage = Number(details?.negotiationPercentage || 0);
  const lowestHighestInsight = hasApprovalStarted(rfqData)
    ? getLowestHighestQuotationInsight(rfqData?.suppliers || [])
    : null;
  const getActivityDuration = (activity) => {
    if (Number(activity?.timeTakenMs) > 0) return activity.timeTakenMs;

    const rounds = details?.roundTrail?.find(
      (supplier) => String(supplier.supplierId) === String(activity?.supplierId)
    )?.rounds;
    const matchingRound = rounds?.find(
      (round) =>
        round.roundNumber > 0 &&
        Number(round.amount) === Number(activity?.negotiatedAmount)
    );

    return matchingRound?.timeTakenMs || 0;
  };

  return (
    <div className="md:hidden min-h-screen bg-gradient-to-r from-[#FFF0E4] to-[#E4E7FF]">
      <div className="sticky top-0 z-20 border-b bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <button type="button" onClick={onBack} className="text-gray-600">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="min-w-0">
            <h2 className="truncate text-base font-bold text-[#1F1E29]">
              {rfqData?.name || `RFQ ${rfqData?.rfqId || ""}`}
            </h2>
          </div>
        </div>
        {tabs}
      </div>

      <div className="space-y-4 px-4 py-4 pb-6">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-[11px] font-semibold uppercase text-gray-500">
              Approval Summary
            </p>
          <p className="mt-1 text-sm font-bold text-[#1F1E29]">RFQ No. {rfqData?.rfqId} | {rfqData?.rfqType || "-"}</p>
          <p className="mt-1 text-sm font-bold text-[#1F1E29]">{rfqData?.projectName || "-"}</p>
        </div>

        {hasApprovalSummary || hasVendorWiseNegotiation ? (
          <>
            {hasApprovalSummary && (
              <>
                <div>
                  <div className="mb-3 flex items-end justify-between gap-2">
                    <div>
                      <h3 className="text-sm font-bold text-[#1F1E29]">
                        Negotiation Outcome
                      </h3>
                      <p className="mt-1 text-xs text-gray-500">
                        {trackedRoundCount} tracked round{trackedRoundCount === 1 ? "" : "s"}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <MobileSummaryCard
                      label="Original Amount"
                      value={`Rs. ${formatAmount(details?.originalAmount)}`}
                    />
                    <MobileSummaryCard
                      label="Final Agreed"
                      value={`Rs. ${formatAmount(details?.finalAgreedAmount)}`}
                    />
                    <MobileSummaryCard
                      label="Total Negotiated"
                      value={`Rs. ${formatAmount(Math.abs(negotiatedAmount))}`}
                      detail={
                        negotiatedAmount > 0
                          ? `${formatAmount(Math.abs(negotiationPercentage))}% reduction`
                          : negotiatedAmount < 0
                            ? `${formatAmount(Math.abs(negotiationPercentage))}% increase`
                            : "No amount movement"
                      }
                      tone={negotiatedAmount > 0 ? "success" : negotiatedAmount < 0 ? "danger" : "neutral"}
                    />
                    <MobileSummaryCard
                      label="Budget"
                      value={getBudgetLabel(budget.status)}
                      detail={
                        budget.status === "NOT_DEFINED"
                          ? ""
                          : `Rs. ${formatAmount(budget.budgetAmount)}`
                      }
                      tone={
                        budget.status === "WITHIN_BUDGET" || budget.status === "AT_BUDGET"
                          ? "success"
                          : budget.status === "BEYOND_BUDGET"
                            ? "danger"
                            : "neutral"
                      }
                    />
                  </div>
                </div>

                <LowestHighestQuotationCard insight={lowestHighestInsight} />

                {bestNegotiator && (
                  <div className="rounded-xl border border-gray-200 bg-white p-3 text-xs text-gray-700">
                    <span className="font-semibold text-[#1F1E29]">Best negotiator:</span>{" "}
                    {bestNegotiator.name} with Rs. {formatAmount(bestNegotiator.netSavings)} net savings
                  </div>
                )}

                {details?.perUserContributions?.length > 0 && (
                  <MobileCollapsibleSection
                    title="Member Contributions"
                    countLabel={`${details.perUserContributions.length} member${
                      details.perUserContributions.length === 1 ? "" : "s"
                    }`}
                  >
                    <div className="space-y-2">
                      {details.perUserContributions.map((contribution) => (
                        <div
                          key={contribution.userId || contribution.name}
                          className="rounded-xl border border-gray-200 bg-white p-3 text-xs"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-semibold text-[#1F1E29]">{contribution.name}</p>
                              <p className="mt-1 text-gray-500">
                                Levels: {contribution.levels?.join(", ") || "-"} | Rounds: {contribution.rounds}
                              </p>
                            </div>
                            <p className="font-semibold text-green-700">
                              Rs. {formatAmount(contribution.netSavings)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </MobileCollapsibleSection>
                )}

                {levelActivities.length > 0 && (
                  <MobileCollapsibleSection
                    title="Approval-level Activity"
                    countLabel={`${levelActivities.length} action${
                      levelActivities.length === 1 ? "" : "s"
                    }`}
                  >
                    <div className="space-y-3">
                      {levelActivities.map((activity, index) => (
                        <MobileActivityCard
                          key={`${activity.level}-${activity.supplierId}-${activity.roundNumber || index}`}
                          activity={activity}
                          index={index}
                          getActivityDuration={getActivityDuration}
                        />
                      ))}
                    </div>
                  </MobileCollapsibleSection>
                )}

                <MobileRoundWiseTrailSection suppliers={details?.roundTrail} />
              </>
            )}

            <MobileVendorWiseNegotiationSection
              suppliers={details?.vendorWiseNegotiationTrail}
              rfqSuppliers={rfqData?.suppliers || []}
            />
          </>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-bold text-[#1F1E29]">
              No negotiation summary available
            </h3>
            <p className="mt-1 text-xs leading-5 text-gray-500">
              There are no tracked negotiation changes available for this RFQ yet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const ApprovalNegotiationSummary = ({ rfqData }) => {
  const details = rfqData?.negotiationDetails;
  const approvalDetails = rfqData?.approvalDetails || [];
  const selectedVendorNames = (rfqData?.suppliers || [])
    .filter((supplier) => supplier?.isAwarded)
    .map((supplier) => supplier.businessName || supplier.supplierId)
    .filter(Boolean)
    .join(", ");
  const creatorActivities = (details?.creatorActivities || []).map((activity) => ({
    ...activity,
    level: "Creator",
  }));
  const creatorNoNegotiationActivity =
    Array.isArray(details?.creatorActivities) && creatorActivities.length === 0
      ? [{
          level: "Creator",
          userName: rfqData?.requesterName || "-",
          supplierName: selectedVendorNames || "-",
          stage: "NO_NEGOTIATION",
          outcome: "NO_NEGOTIATION",
          actionDate: rfqData?.statusLogs?.find(
            (log) => log?.changedStatusTo === "PENDING APPROVAL"
          )?.date,
        }]
      : [];
  const approvalActivities = approvalDetails.flatMap((level, levelIndex) =>
    (level?.negotiationActivities || []).map((activity) => ({
      ...activity,
      level: activity.level || level.level || levelIndex + 1,
    }))
  );
  const noNegotiationActivities = approvalDetails.flatMap((level, levelIndex) => {
    if (String(level?.status).toUpperCase() !== "APPROVED" || level?.negotiationActivities?.length) {
      return [];
    }

    const member = level?.approvedBy || level?.approvers?.[0] || {};
    return [{
      level: level.level || levelIndex + 1,
      userName: member.name || "-",
      supplierName: selectedVendorNames || "-",
      stage: "NO_NEGOTIATION",
      outcome: "NO_NEGOTIATION",
      actionDate: level?.approvedAt,
    }];
  });
  const levelActivities = [
    ...creatorActivities,
    ...creatorNoNegotiationActivity,
    ...approvalActivities,
    ...noNegotiationActivities,
  ];
  const trackedRoundCount = creatorActivities.length + approvalActivities.length;
  const hasApprovalSummary =
    levelActivities.length > 0 ||
    (details?.roundTrail || []).length > 0 ||
    Number(details?.originalAmount || 0) > 0 ||
    Number(details?.finalAgreedAmount || 0) > 0;
  const hasVendorWiseNegotiation = (details?.vendorWiseNegotiationTrail || []).length > 0;
  const isClosedRfq = String(rfqData?.status || "").toUpperCase() === "CLOSED";
  const canShowApprovalSummary = Boolean(details && hasApprovalSummary);
  const canShowVendorWiseNegotiation = Boolean(details && hasVendorWiseNegotiation);

  if (!isClosedRfq && !canShowApprovalSummary && !canShowVendorWiseNegotiation) {
    return null;
  }

  const budget = details?.budget || {};
  const bestNegotiator = details?.bestNegotiator;
  const negotiatedAmount = Number(details?.totalNegotiatedAmount || 0);
  const negotiationPercentage = Number(details?.negotiationPercentage || 0);
  const lowestHighestInsight = hasApprovalStarted(rfqData)
    ? getLowestHighestQuotationInsight(rfqData?.suppliers || [])
    : null;
  const getActivityDuration = (activity) => {
    if (Number(activity?.timeTakenMs) > 0) return activity.timeTakenMs;

    const rounds = details?.roundTrail?.find(
      (supplier) => String(supplier.supplierId) === String(activity?.supplierId)
    )?.rounds;
    const matchingRound = rounds?.find(
      (round) =>
        round.roundNumber > 0 &&
        Number(round.amount) === Number(activity?.negotiatedAmount)
    );

    return matchingRound?.timeTakenMs || 0;
  };

  return (
    <section className="mt-4">
      <RfqSavingsBasisSection rfqData={rfqData} />

      {canShowApprovalSummary && (
        <>
          <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold text-[#1F1E29]">Approval Negotiation Summary</h3>
              <p className="mt-1 text-xs text-gray-500">
                Approval-level amount movement and member contribution
              </p>
            </div>
            <p className="text-xs text-gray-500">
              {trackedRoundCount} tracked negotiation round{trackedRoundCount === 1 ? "" : "s"}
            </p>
          </div>

          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryItem label="Original Amount" value={`Rs. ${formatAmount(details.originalAmount)}`} />
            <SummaryItem
              label="Final Agreed Amount"
              value={`Rs. ${formatAmount(details.finalAgreedAmount)}`}
            />
            <SummaryItem
              label="Total Negotiated"
              value={`Rs. ${formatAmount(Math.abs(negotiatedAmount))}`}
              detail={
                negotiatedAmount > 0
                  ? `${formatAmount(Math.abs(negotiationPercentage))}% reduction`
                  : negotiatedAmount < 0
                    ? `${formatAmount(Math.abs(negotiationPercentage))}% increase`
                    : "No amount movement"
              }
              className={
                negotiatedAmount > 0
                  ? "bg-green-200"
                  : negotiatedAmount < 0
                    ? "bg-red-200"
                    : "bg-white"
              }
            />
            <SummaryItem
              label="Budget Comparison"
              value={getBudgetLabel(budget.status)}
              detail={
                budget.status === "NOT_DEFINED"
                  ? ""
                  : `Budget Rs. ${formatAmount(budget.budgetAmount)} | Variance Rs. ${formatAmount(
                      budget.variance
                    )}`
              }
              className={
                budget.status === "WITHIN_BUDGET" || budget.status === "AT_BUDGET"
                  ? "bg-green-200"
                  : budget.status === "BEYOND_BUDGET"
                    ? "bg-red-200"
                    : "bg-white"
              }
            />
          </div>

          <LowestHighestQuotationCard insight={lowestHighestInsight} />
        </>
      )}

      {canShowApprovalSummary && bestNegotiator && (
        <div className="mt-3 border border-gray-200 bg-white px-4 py-3 text-xs text-gray-700">
          <span className="font-semibold text-[#1F1E29]">Best negotiator:</span>{" "}
          {bestNegotiator.name} with Rs. {formatAmount(bestNegotiator.netSavings)} net savings
        </div>
      )}

      {canShowApprovalSummary && details?.perUserContributions?.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <h4 className="mb-2 text-xs font-semibold uppercase text-gray-500">Member Contributions</h4>
          <table className="min-w-full border-collapse bg-white text-xs">
            <thead>
              <tr className="border border-gray-200 text-left text-gray-500">
                <th className="px-3 py-2 font-medium">Member</th>
                <th className="px-3 py-2 font-medium">Levels</th>
                <th className="px-3 py-2 font-medium">Rounds</th>
                <th className="px-3 py-2 font-medium">Net Savings</th>
              </tr>
            </thead>
            <tbody>
              {details.perUserContributions.map((contribution) => (
                <tr key={contribution.userId || contribution.name} className="border border-gray-200">
                  <td className="px-3 py-2">{contribution.name}</td>
                  <td className="px-3 py-2">{contribution.levels?.join(", ") || "-"}</td>
                  <td className="px-3 py-2">{contribution.rounds}</td>
                  <td className="px-3 py-2">Rs. {formatAmount(contribution.netSavings)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canShowApprovalSummary && levelActivities.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <SectionTitle className="mb-2">
            Approval-level Activity
          </SectionTitle>
          <table className="min-w-full border-collapse bg-white text-xs">
            <thead>
              <tr className="border border-gray-200 text-left text-gray-500">
                <th className="px-3 py-2 font-medium">Level</th>
                <th className="px-3 py-2 font-medium">Approvar</th>
                <th className="px-3 py-2 font-medium">Vendor</th>
                <th className="px-3 py-2 font-medium">Stage</th>
                <th className="px-3 py-2 font-medium">Movement</th>
                <th className="px-3 py-2 font-medium">Outcome</th>
                <th className="px-3 py-2 font-medium">Time Taken</th>
                <th className="px-1 py-2 font-medium">Action Date</th>
              </tr>
            </thead>
            <tbody>
              {levelActivities.map((activity, index) => (
                <tr
                  key={`${activity.level}-${activity.supplierId}-${activity.roundNumber || index}`}
                  className="border border-gray-200"
                >
                  <td className="px-3 py-2">{activity.level}</td>
                  <td className="px-3 py-2">{activity.userName || "-"}</td>
                  <td className="px-3 py-2">{activity.supplierName || activity.supplierId || "-"}</td>
                  <td className="px-3 py-2">
                    {activity.stage === "RFQ_CREATOR_SELECTION"
                      ? "Creator Selection"
                      : activity.stage === "NO_NEGOTIATION"
                        ? "Approval"
                        : "Approval Negotiation"}
                  </td>
                  <td className="px-3 py-2">
                    {activity.outcome === "NO_NEGOTIATION"
                      ? "-"
                      : `Rs. ${formatAmount(activity.previousAmount)} to Rs. ${formatAmount(
                          activity.negotiatedAmount
                        )}`}
                  </td>
                  <td
                    className={`px-3 py-2 ${
                      activity.savings > 0
                        ? "text-green-700"
                        : activity.savings < 0
                          ? "text-red-700"
                          : "text-gray-600"
                    }`}
                  >
                    {activity.outcome === "SAVING"
                      ? `Saving Rs. ${formatAmount(activity.savings)}`
                      : activity.outcome === "INCREASE"
                        ? `Increase Rs. ${formatAmount(Math.abs(activity.savings))}`
                        : activity.outcome === "NO_NEGOTIATION"
                          ? "No negotiation performed"
                          : "No change"}
                  </td>
                  <td className="px-3 py-2">{formatDuration(getActivityDuration(activity))}</td>
                  <td className="px-1 py-2">
                    {formatIstDateTime(activity.actionDate || activity.negotiatedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canShowApprovalSummary && (
        <NegotiationTrailSection
          title="Round-wise Trail"
          description="Selected or awarded vendor amount movement used for the approval negotiation summary."
          suppliers={details?.roundTrail}
        />
      )}

      {canShowVendorWiseNegotiation && (
        <VendorWiseNegotiationSection
          suppliers={details?.vendorWiseNegotiationTrail}
          rfqSuppliers={rfqData?.suppliers || []}
        />
      )}
    </section>
  );
};

export default ApprovalNegotiationSummary;
